import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi, extractFieldErrors, type FieldErrors } from '@/lib/api';
import { clearAllIndexedDB } from '@/lib/db';
import { hasAccessToken, isProtectedPath } from '@/lib/auth-utils';
import type { AuthUser, AuthContextType, UserRole } from '@/lib/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapApiUser(data: {
  id: string;
  email: string;
  full_name: string;
  role?: string;
  role_name?: string;
}): AuthUser {
  const role = (data.role_name || data.role || 'doctor') as UserRole;
  return {
    id: data.id,
    email: data.email,
    name: data.full_name,
    role,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const clearSession = useCallback(() => {
    setUser(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }, []);

  const hydrateSession = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await authApi.me();
      setUser(mapApiUser(data));
    } catch {
      clearSession();
    } finally {
      setIsLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    void hydrateSession();
  }, [hydrateSession]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      if (!hasAccessToken()) {
        clearSession();
        if (isProtectedPath(window.location.pathname)) {
          window.location.replace('/');
        }
        return;
      }
      void hydrateSession();
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [clearSession, hydrateSession]);

  const login = async (
    email: string,
    password: string,
    selectedRole?: UserRole,
  ): Promise<UserRole | false> => {
    setFieldErrors({});
    try {
      const { data } = await authApi.login({
        email,
        password,
        role: selectedRole ?? undefined,
      });
      if (!data.access || !data.refresh) {
        return false;
      }
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      const role = (data.user.role_name || data.user.role) as UserRole;
      setUser({
        id: data.user.id,
        email: data.user.email,
        name: data.user.full_name,
        role,
      });
      setIsLoading(false);
      return role;
    } catch (err: any) {
      const errors = extractFieldErrors(err);
      if (errors && Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return false;
      }
      throw err;
    }
  };

  const logout = async () => {
    clearSession();
    await clearAllIndexedDB();
  };

  const isAuthenticated = Boolean(user && hasAccessToken());

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        logout,
        fieldErrors,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
